/**
 * Shared design tokens for the unified App Bar, Impersonation Search,
 * Sign In page, Footer, System Screens and banners.
 *
 * Per-app values (bar color, accent, light/dark) live in alliance-apps.ts.
 * Values come from the Claude Design "Unified Alliance App System"
 * component specs.
 */

/** Below md (900px) every app switches to the mobile layout */
export const ALLIANCE_MOBILE_BREAKPOINT = 'md';

export const APP_BAR = {
  heightDesktop: 64,
  heightMobile: 56,
  paddingDesktop: 16,
  paddingMobile: 12,
  /** Gap between controls in the bar */
  gap: 8,
  /** Bar controls: 40px tall, radius 10, 1px border. The bar is always dark */
  controlHeight: 40,
  controlRadius: 10,
  controlBorder: '#ffffff33',
  controlHover: '#ffffff14',
  controlOpen: '#ffffff1f',
  /** Divider between the actions slot and the user control */
  slotDivider: '#ffffff26',
  /** Initials tile in the user control */
  avatarSize: 28,
  avatarRadius: 8,
  /** User menu and launcher Paper */
  menuRadius: 14,
  menuOffset: 8,
  userMenuWidth: 340,
  launcherWidth: 360,
  /** Bottom sheets below md */
  sheetRadius: 20,
  /** App tiles in the launcher and on the Sign In page */
  appTileSize: 40,
  appTileRadius: 10,
  badgeSize: 18,
};

/**
 * Status colors. These never take the app's accent, so the state reads the
 * same in every app.
 */
export const STATUS_COLORS = {
  staff: {
    main: '#FFA726',
    /** 14% tint behind the user control */
    tint: '#FFA72624',
    onMain: '#241300',
    /** Line 2 of the user control ("for NAA168026") */
    text: '#FFB74D',
  },
  impersonating: {
    main: '#c6ff00',
    tint: '#c6ff0026',
    onMain: '#1a2600',
  },
};

/** Tones for System Screens (icon tile, eyebrow, bullets, date chip) */
export const SYSTEM_TONES = {
  light: {
    info: { fg: '#0277bd', bg: '#0288d114', line: '#0288d140' },
    warning: { fg: '#b45309', bg: '#ed6c0214', line: '#ed6c0240' },
    error: { fg: '#c62828', bg: '#d32f2f12', line: '#d32f2f40' },
    neutral: { fg: '#000000a6', bg: '#0000000d', line: '#00000026' },
  },
  dark: {
    info: { fg: '#4fc3f7', bg: '#4fc3f71f', line: '#4fc3f74d' },
    warning: { fg: '#ffb74d', bg: '#ffb74d1f', line: '#ffb74d4d' },
    error: { fg: '#ff8a80', bg: '#ff8a801f', line: '#ff8a804d' },
    neutral: { fg: '#ffffffb3', bg: '#ffffff14', line: '#ffffff33' },
  },
};

/** Tones for the banner slot under the App Bar */
export const BANNER_TONES = {
  light: {
    info: {
      bg: '#e5f6fd',
      border: '#0288d140',
      text: '#014361',
      icon: '#0288d1',
    },
    warning: {
      bg: '#fff4e5',
      border: '#ed6c0240',
      text: '#5f2b00',
      icon: '#e65100',
    },
    error: {
      bg: '#fdeded',
      border: '#d32f2f40',
      text: '#5f2120',
      icon: '#d32f2f',
    },
  },
  dark: {
    info: {
      bg: '#0c2733',
      border: '#4fc3f740',
      text: '#b3e5fc',
      icon: '#4fc3f7',
    },
    warning: {
      bg: '#2e2210',
      border: '#ffb74d40',
      text: '#ffe0b2',
      icon: '#ffb74d',
    },
    error: {
      bg: '#321616',
      border: '#ff8a8040',
      text: '#ffcdd2',
      icon: '#ff8a80',
    },
  },
};

/**
 * Starting thresholds (approved Oct 1 2026, to be tuned during
 * implementation).
 */
export const ALLIANCE_THRESHOLDS = {
  /** Sign in button shows its error if the Hosted UI redirect hasn't happened */
  signInFailureMs: 8000,
  /** Connection banner: this many failed requests... */
  connectionFailures: 3,
  /** ...within this window */
  connectionWindowMs: 30000,
  /** Connection banner clears after this many successful requests */
  connectionRecoveries: 2,
  /** In-shell page loading bar waits this long so fast routes never flash it */
  pageLoadingDelayMs: 150,
};

/** Impersonation search limits and storage */
export const IMPERSONATION_SEARCH = {
  minLength: 3,
  maxLength: 50,
  recentLimit: 5,
  /** localStorage key, per device */
  recentStorageKey: 'naa-imp-recent',
};

/** Help Desk block on System Screens */
export const HELP_DESK = {
  email: 'help@naaleads.com',
  hours: 'Monday–Friday, 8AM–5PM EST',
  include:
    'Please include your name, NAA Number, and the name of your upline/agency manager.',
};
